import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Typography } from 'antd';

export interface PipelineStageActivityItem {
  stageId: number;
  stageLabel: string;
  dotColor: string;
  actionCount: number;
  activeRepCount: number;
  description: string;
}

const { Text } = Typography;

interface PipelineStageBarChartProps {
  stages: PipelineStageActivityItem[];
  height?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: PipelineStageActivityItem;
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const stage = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          padding: '10px 14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          {stage.stageLabel}
        </Text>
        <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary)' }}>
          <div>{stage.actionCount} actions</div>
          <div>{stage.activeRepCount} active reps</div>
        </div>
      </div>
    );
  }
  return null;
};

export const PipelineStageBarChart: React.FC<PipelineStageBarChartProps> = ({
  stages,
  height = 250,
}) => {
  if (!stages || stages.length === 0) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Text type="secondary">No pipeline activity data available</Text>
      </div>
    );
  }

  // Transform data for Recharts
  const chartData = stages.map((stage) => ({
    ...stage,
    name: stage.stageLabel,
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={chartData}
        margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 12, fill: '#666' }}
          tickLine={false}
          axisLine={{ stroke: '#f0f0f0' }}
          interval={0}
          angle={-20}
          textAnchor="end"
          height={50}
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#666' }}
          tickLine={false}
          axisLine={false}
          allowDecimals={false}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
        <Bar dataKey="actionCount" radius={[4, 4, 0, 0]} maxBarSize={50}>
          {chartData.map((entry) => (
            <Cell key={entry.stageId} fill={entry.dotColor} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default PipelineStageBarChart;
